import { use, useCallback, useEffect, useState } from "react";
import {
  FlightSearchParams,
  Itinerary,
  SearchResponse,
  TransportItem,
  TransportMode,
  TransportSearch,
  TransportSearchParams,
} from "../../types";
import { addDaysToDate, formatDate, isFutureDate } from "../../functions/Utils";
import LoadingMessage from "../LoadingMessage";
import api from "../../api";
import { apiRoutes } from "../../routes/routes";
import FlightCard from "../Flights/FlightCard";
import {
  getTransportIcon,
  haversineDistance,
  formatCoords,
} from "../../functions/ItineraryUtils";
import OfferCard from "./TransportCard";
import TransportSearchResults from "./TransportSearchResults";
import { getAgents } from "../../functions/Query";
import TransportSearchHeader from "./TransportSearchHeader";

interface Props {
  itinerary: Itinerary;
  cities: any;
}

const TransportSearchPanel: React.FC<Props> = ({ itinerary, cities }) => {
  const [transportSearches, setTransportSearches] = useState<
    TransportSearch[] | []
  >([]);
  const [selectedSearch, setSelectedSearch] = useState<TransportSearch | null>(
    null
  );
  const [searchTransportTypes, setSearchTransportTypes] = useState<
    string[] | []
  >([]);
  const [selectedSearchType, setSelectedSearchType] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState<number[] | []>([]);
  const [completed, setCompleted] = useState(0);
  const [searchResponse, setSearchResponse] = useState<SearchResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<"outbound" | "inbound">(
    "outbound"
  );

  const createSearchList = () => {
    const searchList: TransportSearch[] = [];
    const keys: Record<string, number> = {};

    if (!itinerary?.data?.departure_date || !itinerary?.data?.transports)
      return;

    itinerary.data.transports.forEach((transport, index) => {
      const key = `${transport.origin}-${transport.destination}`;
      const returnKey = `${transport.destination}-${transport.origin}`;

      if (returnKey in keys) {
        const outboundItem = searchList[keys[returnKey]];
        outboundItem.return_date = addDaysToDate(
          itinerary.data.departure_date!,
          transport.trip_day_id - 1
        );
      } else {
        keys[key] = searchList.length;
        const item = {
          id: searchList.length,
          origin: cities[transport.origin],
          destination: cities[transport.destination],
          transport_mode: transport.transport_mode,
          distance: haversineDistance(
            formatCoords(cities[transport.origin].coordinates),
            formatCoords(cities[transport.destination].coordinates),
            "km",
            0
          ),
          departure_date: addDaysToDate(
            itinerary.data.departure_date!,
            transport.trip_day_id - 1
          ),
        };
        item.transport_mode = getTransportModes(item) as TransportMode[];
        searchList.push(item);
      }
    });

    setTransportSearches(searchList);
  };

  const fetchTransports = async (
    search: TransportSearch,
    searchType: string
  ) => {
    if (!search) return;

    setLoading(true);
    setError(null);
    setSearchResponse([]);
    setCompleted(0);
    setSources([]);

    try {
      if (searchType == "flight") {
        if (
          !search?.origin?.iata_city_code ||
          !search?.destination?.iata_city_code
        )
          return;

        const flightParams: FlightSearchParams = {
          origin: search.origin.iata_city_code,
          destination: search.destination.iata_city_code,
          departure_date: search.departure_date,
          return_date: search.return_date ? search.return_date : "",
          adults: 1,
          children: 0,
          infants: 0,
        };

        const res = await api.get(apiRoutes.search_flights, {
          params: flightParams,
        });

        const response: SearchResponse = res.data ? res.data : [];


        setSearchResponse((prev) => [...prev, response]);
      } else {
        const source_agents: number[] =
          searchType !== "ferry"
            ? getAgents(
                search.origin.country_code,
                search.destination.country_code
              )
            : [9];

        setSources(source_agents);

        source_agents.forEach(async (source: number) => {
          const transportParams: TransportSearchParams = {
            mode: searchType,
            origin: search.origin.cityname,
            destination: search.destination.cityname,
            departure_date: search.departure_date,
            return_date: search.return_date ? search.return_date : "",
            origin_coordinates: `${search.origin.coordinates[0]},${search.origin.coordinates[1]}`,
            destination_coordinates: `${search.destination.coordinates[0]},${search.destination.coordinates[1]}`,
            origin_country_code: search.origin.country_code,
            destination_country_code: search.destination.country_code,
            adults: 1,
            children: 0,
            infants: 0,
          };

          const res = await api.get(
            `${apiRoutes.multi_search_transports}?agent_id=${source}`,
            { params: transportParams }
          );

          const response: SearchResponse = res.data
            ? res.data
            : [];
          setSearchResponse((prev) => [...prev, response]);

          setCompleted((prev) => prev + 1);
        });

        // setOffers(getTopOffers(res.data.results.outbound, { price: 1, duration: 1, efficiency: 2 }) || []);
      }
    } catch (err) {
      setError("Une erreur est survenue lors de la recherche de vols.");
    } finally {
      setLoading(false);
    }
  };

  const getTransportModes = (search: TransportSearch) => {
    const transportTypes = new Set<string>();

    search.transport_mode.forEach((mode) => {
      if (["train", "bus", "car"].includes(mode)) {
        transportTypes.add("ground");
      } else {
        transportTypes.add(mode);
      }
    });

    if (
      search &&
      search.origin.iata_city_code &&
      search.destination.iata_city_code
    ) {
      transportTypes.add("flight");

      if (
        !search.transport_mode.includes("ferry") &&
        search.distance &&
        search.distance < 400
      ) {
        transportTypes.delete("flight");
      }
    }

    return Array.from(transportTypes);
  };

  const launchSearch = async (
    search: TransportSearch,
    searchType: string | null = null
  ) => {
    if (!isFutureDate(search.departure_date)) {
      return false;
    }

    const arrTransportTypes = getTransportModes(search);
    const searchTransportType = searchType ? searchType : arrTransportTypes[0];
    setSearchTransportTypes(arrTransportTypes);
    setSelectedSearchType(searchTransportType);

    setSelectedSearch(search);
    setSearchResponse([]);

    if (search) {
      fetchTransports(search, searchTransportType);
    } else {
      setError("Params error!");
    }
  };

  useEffect(() => {
    setTransportSearches([]);
    createSearchList();
  }, []);

  return (
    <div className="d-flex h-100">
      {/* Menu latéral */}
      <div className="sidebar d-flex flex-column" style={{ width: "250px" }}>
        <div className="h-100">
          <ul className="list-group h-100 overflow-auto">
            {transportSearches &&
              transportSearches.map((search) => (
                <li
                  key={search.id}
                  className={`list-group-item list-group-item-action ${
                    selectedSearch?.id === search.id ? "active" : ""
                  }
                ${isFutureDate(search.departure_date) ? "" : "text-gray-500"}
                `}
                  style={{ cursor: "pointer" }}
                  onClick={() => launchSearch(search)}
                >
                  <b className="title">
                    {search.origin.cityname} → {search.destination.cityname}
                  </b>
                  <br />
                  <small>
                    {search.departure_date
                      ? formatDate(search.departure_date)
                      : ""}
                  </small>
                  {search.return_date && (
                    <div>
                      <b className="title">
                        {search.destination.cityname} → {search.origin.cityname}
                      </b>
                      <br />
                      <small>
                        {search.return_date
                          ? formatDate(search.return_date)
                          : ""}
                      </small>
                    </div>
                  )}
                  {search.transport_mode && (
                    <div className="float-end">
                      {search.transport_mode.map((mode, index) => (
                        <span
                          className="badge bg-primary me-1"
                          key={`transport-icon-${index}`}
                        >
                          {getTransportIcon(mode)}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
          </ul>
        </div>
      </div>

      {/* Zone principale */}
      <div className="main-content flex-grow-1 ps-4 overflow-auto h-100">
        {selectedSearch && searchTransportTypes && (
          <div className="mb-3">
            {searchTransportTypes.map((mode) => (
              <div
                onClick={() => launchSearch(selectedSearch, mode)}
                className={`me-1 btn ${
                  selectedSearchType && selectedSearchType === mode
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
              >
                {getTransportIcon(mode)} {mode}
              </div>
            ))}
          </div>
        )}

        {selectedSearch ? (
          <div className="rounded bg-white">
            {loading && <LoadingMessage />}

            {!loading && selectedSearchType == "flight" && (
              <div>
                <TransportSearchResults
                  responses={searchResponse}
                  renderResult={(flight) => <FlightCard flight={flight} />}
                  completed={1}
                />
                {error && <div className="text-danger">{error}</div>}
              </div>
            )}

            {!loading &&
              (selectedSearchType == "ferry" ||
                selectedSearchType == "ground") && (
                <div>
                  {selectedSearch.return_date && (
                    <TransportSearchHeader
                      search={selectedSearch}
                      direction={direction}
                      transportType={selectedSearchType}
                    />
                  )}

                  <TransportSearchResults
                    responses={searchResponse}
                    renderResult={(offer) => <OfferCard offer={offer} />}
                    completed={completed / sources.length}
                  />
                  {error && <div className="text-danger">{error}</div>}
                </div>
              )}
          </div>
        ) : (
          <p className="text-muted">
            Cliquez sur une recherche à gauche pour voir les résultats.
          </p>
        )}
      </div>
    </div>
  );
};

export default TransportSearchPanel;
